'use client';

import { Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { QuickActionsTriggerProps } from './types';

/**
 * QuickActionsTrigger Component
 *
 * Styled button that opens the Quick Actions modal.
 * Replaces the SearchTrigger in the sidebar.
 *
 * Design specs:
 * - Border radius: 8px (rounded-lg)
 * - Background: white (#FFF)
 * - Box shadow: 0 0 2px 0 rgba(28, 40, 64, 0.18), 0 1px 3px 0 rgba(24, 41, 75, 0.04)
 */
export function QuickActionsTrigger({
  onClick,
  className,
  compact = false
}: QuickActionsTriggerProps) {
  const button = (
    <Button
      variant="ghost"
      size={compact ? 'icon' : 'sm'}
      onClick={onClick}
      className={cn(
        compact
          ? 'h-8 w-8' // Icon button in compact mode
          : 'h-8 w-full justify-start rounded-lg border border-black/5 bg-white',
        className
      )}
    >
      <Search className={cn('h-4 w-4', !compact && 'mr-2')} />
      {!compact && (
        <>
          <span>Search</span>
          <kbd className="bg-muted text-muted-foreground text-12px pointer-events-none ml-auto inline-flex h-5 items-center gap-1 rounded border px-1.5 font-mono font-medium opacity-100 select-none">
            <span className="text-xs">⌘</span>K
          </kbd>
        </>
      )}
    </Button>
  );

  if (!compact) {
    return button;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="right">Search (⌘K)</TooltipContent>
    </Tooltip>
  );
}
